import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, cwave, envelope, inOut, inQuad, keys, outCubic, outExpo, outQuad, phase, settle, smooth, span, wave, type Ease } from '../curves.ts';
import { timeline, type Ch, type KeyPose } from '../pose.ts';

/*
 * Polilla del sueño (models/moth.ts): a great moth of the era III swarm. It hovers at its rest height and every
 * clip keeps it in the air except `die`, which spirals it down to the ground.
 *
 * Wings are authored for the right side (+X) and mirrored: on `foreR`/`hindR` +Z lifts the wing, +Y sweeps it
 * back, +X turns its leading edge down. The hindwing follows the forewing a beat late and a little shallower.
 * +X pitches the body and head nose down, curls the abdomen under, lowers the antennae and swings the legs back.
 * `dust` is the cloud of glowing sleep-dust round the body: the clips swirl it and swell it.
 *
 * The beat is a moth's, not a bird's: huge and soft, the wings nearly clapping over the back and sweeping
 * down and forward, the body heaving up on every stroke and dropping between them, never quite on course.
 */

type B = 'root' | 'body' | 'head' | 'antennae' | 'abdomen' | 'foreL' | 'hindL' | 'foreR' | 'hindR' | 'legs' | 'dust';
type P = KeyPose<B>;

const sin = Math.sin;
/** The ground under the hovering origin (models/moth.ts `MOTH_HOVER` and the body's height), less the body lying on it. */
const GROUND = -0.95;
/** The strike's ease: it accelerates into the wrap but spreads over the 30 Hz samples. */
const snap: Ease = (k) => k ** 1.5;

const mirror = (c: Ch): Ch => [c[0], -c[1], -c[2], -(c[3] ?? 0), c[4] ?? 0, c[5] ?? 0, c[6] ?? 1];
/** All four wings from the right fore and hind ones. */
const wings = (fore: Ch, hind: Ch = fore): P => ({ foreR: fore, hindR: hind, foreL: mirror(fore), hindL: mirror(hind) });

/** A side's wing pose: forewing and hindwing rotations. */
interface W { fx: number; fy: number; fz: number; hx: number; hy: number; hz: number }

/** The forewing over a beat at `p` (0 = top of the stroke, wings high; loops at 1). `a` deepens the downstroke. */
const foreAt = (p: number, a: number): [number, number, number] => [
  keys(p, [[0, -0.12], [0.2, 0.22 * a], [0.45, 0.3 * a], [0.7, -0.25 * a], [1, -0.12]]),
  keys(p, [[0, 0.22], [0.25, -0.05 * a], [0.48, -0.2 * a], [0.75, 0.3], [1, 0.22]]),
  keys(p, [[0, 1.05], [0.46, 1.05 - 1.8 * a, inOut], [1, 1.05, inOut]]),
];
/** One wingbeat of a side: the hindwing trails the forewing a little and beats shallower. */
const beatAt = (p: number, a = 1): W => {
  const [fx, fy, fz] = foreAt(p, a);
  const [hx, hy, hz] = foreAt((p + 0.95) % 1, a * 0.92);
  return { fx, fy, fz, hx: hx * 0.6, hy: hy + 0.06, hz: hz * 0.94 };
};
const wingR = (b: Bones<B>, w: W): void => {
  b.foreR.rot(w.fx, w.fy, w.fz);
  b.hindR.rot(w.hx, w.hy, w.hz);
};
const wingL = (b: Bones<B>, w: W): void => {
  b.foreL.rot(w.fx, -w.fy, -w.fz);
  b.hindL.rot(w.hx, -w.hy, -w.hz);
};
const both = (b: Bones<B>, w: W): void => {
  wingR(b, w);
  wingL(b, w);
};
/** What a beat does to the body: heaved up on the downstroke, dropping between strokes; the abdomen pumps. */
const heave = (b: Bones<B>, p: number, amp = 1): void => {
  b.root.pos(0, 0.05 * amp * sin(TAU * (p - 0.3)), 0);
  b.body.rot(-0.07 * amp * sin(TAU * (p - 0.1)));
  b.head.rot(0.05 * amp * sin(TAU * (p - 0.1)));
  b.abdomen.rot(0.12 * amp * sin(TAU * (p - 0.25)));
  b.antennae.rot(0.06 * amp * sin(TAU * (p - 0.35)));
  b.legs.rot(0.1 * amp * sin(TAU * (p - 0.4)));
};

/** A smooth bump over a loop phase centred at `c`, `w` wide (loop-safe accents: a twitch, a jink). */
const bump = (p: number, c: number, w: number): number => {
  let d = Math.abs(p - c);
  d = Math.min(d, 1 - d);
  return d >= w ? 0 : smooth(1 - d / w);
};

/** Uneven beats through one loop: their start phases (the last is 1) and how deep each strokes. */
const uneven = (P: number, starts: readonly number[], amps: readonly number[]): { p: number; a: number } => {
  let i = 0;
  while (i < starts.length - 2 && P >= starts[i + 1]!) i++;
  const s0 = starts[i]!, s1 = starts[i + 1]!;
  return { p: (P - s0) / (s1 - s0), a: amps[i]! };
};

/** Hovering: body tipped up, abdomen hanging, legs dangling; five soft uneven beats; drifting, antennae twitching. */
const mothIdle = (): Clip<B> =>
  clip<B>('idle', 1.5, true, (b, t) => {
    const P = phase(t, 1.5);
    const { p, a } = uneven(P, [0, 0.19, 0.4, 0.6, 0.8, 1], [1, 0.82, 1.1, 0.9, 0.95]);
    both(b, beatAt(p, a * 0.9));
    heave(b, p, a);
    b.body.rot(-0.32 + 0.05 * wave(P, 0.2));
    b.head.rot(0.2, 0.18 * bump(P, 0.3, 0.15) - 0.14 * bump(P, 0.75, 0.15));
    b.antennae.rot(-0.15 + 0.2 * bump(P, 0.55, 0.06) - 0.12 * bump(P, 0.15, 0.08), 0, 0);
    b.abdomen.rot(0.3 + 0.05 * wave(P, 0.4, 2));
    b.legs.rot(-0.45 + 0.08 * wave(P, 0.15));
    b.root.pos(0.05 * wave(P, 0.1) + 0.02 * wave(P, 0.3, 3), 0.04 * wave(P, 0.35, 2), 0.03 * cwave(P, 0.2)).rot(0, 0.14 * wave(P, 0.05), 0.07 * wave(P, 0.25, 2));
    b.dust.rot(0, 0.35 * wave(P, 0.1), 0.06 * wave(P, 0.3)).scale(1 + 0.08 * wave(P, 0.2, 2));
  });

/** Flying on: four beats of uneven tempo and depth, jinking and dropping between them like a moth at a lamp. */
const mothWalk = (dur = 1.2): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const P = phase(t, dur);
    const { p, a } = uneven(P, [0, 0.22, 0.47, 0.66, 1], [1.05, 0.85, 1.15, 0.9]);
    both(b, beatAt(p, a));
    heave(b, p, a * 1.2);
    // Jinks: a lurch to one side, a sudden drop, a yaw; never twice the same way within the loop.
    b.root
      .pos(0.07 * wave(P, 0.1) + 0.035 * wave(P, 0.3, 3), 0.04 * wave(P, 0.6, 2) - 0.05 * bump(P, 0.5, 0.1), 0.03 * wave(P, 0.2, 2))
      .rot(0, 0.16 * wave(P, 0.05) + 0.06 * wave(P, 0.4, 3), 0.14 * wave(P, 0.55) + 0.05 * wave(P, 0.1, 2));
    b.body.rot(0.06 + 0.05 * wave(P, 0.2, 2), 0, 0.06 * wave(P, 0.7));
    b.head.rot(-0.08, 0.1 * wave(P, 0.7), 0);
    b.antennae.rot(0.12 + 0.05 * wave(P, 0.3, 2));
    b.abdomen.rot(0.08, 0.1 * wave(P, 0.45), 0);
    b.legs.rot(0.35);
    b.dust.rot(0, 0.25 * wave(P, 0.3), 0).pos(0, 0, -0.06).scale(1.05 + 0.08 * wave(P, 0.1, 2));
  });

/** Wings flung high over its back, it falls on its prey and wraps it (the blow at 0.13 s), holds it, lets go. */
const mothAttack = (): Clip<B> =>
  clip<B>('attack', 0.78, false, (b, t) => {
    const cocked: P = {
      root: [0, 0, 0, 0, 0.08, -0.06], body: [-0.55, 0, 0], head: [-0.2, 0, 0], antennae: [-0.45, 0, 0], abdomen: [0.45, 0, 0], legs: [-0.9, 0, 0],
      ...wings([-0.15, 0.3, 1.2], [-0.1, 0.36, 1.12]),
    };
    const wrap: P = {
      root: [0, 0, 0, 0, -0.28, 0.34], body: [0.75, 0, 0], head: [0.3, 0, 0], antennae: [0.45, 0, 0], abdomen: [0.7, 0, 0], legs: [-1.1, 0, 0],
      dust: [0, 0.5, 0, 0, -0.05, 0.1, 1.7],
      ...wings([0.45, -0.75, -1.15], [0.35, -0.55, -1.05]),
    };
    timeline(b, t, [
      [0, cocked],
      [0.05, { ...cocked, root: [0, 0, 0, 0, 0.11, -0.08], body: [-0.65, 0, 0], ...wings([-0.2, 0.36, 1.32], [-0.12, 0.42, 1.22]) }, outQuad],
      [0.13, wrap, snap],
      [0.32, { ...wrap, root: [0, 0, 0, 0, -0.31, 0.36], dust: [0, 0.8, 0, 0, -0.08, 0.1, 1.9], ...wings([0.5, -0.82, -1.28], [0.4, -0.6, -1.16]) }, inOut],
      [0.46, { root: [0, 0, 0, 0, 0.04, 0.14], body: [-0.25, 0, 0], head: [-0.1, 0, 0], abdomen: [0.25, 0, 0], legs: [-0.4, 0, 0], dust: [0, 0.9, 0, 0, 0, 0, 1.3], ...wings([-0.1, 0.25, 0.95], [-0.05, 0.3, 0.85]) }, outQuad],
      [0.62, { root: [0, 0, 0, 0, 0.06, 0.05], body: [-0.1, 0, 0], ...wings([0.25, -0.15, -0.55], [0.18, -0.1, -0.45]) }, inOut],
      [0.78, {}, inOut],
    ], { head: 0.015, antennae: 0.03, abdomen: 0.03, legs: 0.02, hindL: 0.015, hindR: 0.015, dust: 0.04 });
  }, (t) => {
    // The squeeze: it hugs in round its prey.
    const k = envelope(t, 0.12, 0.16, 0.3, 0.42);
    return [1 - 0.06 * k, 1 + 0.04 * k, 1 - 0.04 * k];
  });

/** Struck in the air: thrown back and rolled, wings flung up out of time, antennae swept; it rights itself. */
const mothHit = (): Clip<B> =>
  clip<B>('hit', 0.42, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.05, {
        root: [0, 0.3, 0, 0, 0.07, -0.18], body: [-0.6, 0.15, 0.45], head: [-0.45, -0.3, 0.2], antennae: [-0.6, 0, 0], abdomen: [-0.35, 0, 0.3], legs: [-0.7, 0, 0], dust: [0, 0, 0, 0, 0, 0, 1.35],
        foreR: [-0.2, 0.2, 1.3], hindR: [-0.1, 0.3, 1.15], foreL: [0.2, -0.3, -0.25], hindL: [0.1, -0.35, -0.1],
      }, outExpo],
      [0.18, { root: [0, -0.08, 0, 0, -0.03, -0.1], body: [0.15, -0.05, -0.15], head: [0.2, 0.1, -0.05], antennae: [0.25, 0, 0], abdomen: [0.25, 0, 0], legs: [-0.3, 0, 0], ...wings([0.2, -0.1, -0.6], [0.15, -0.05, -0.5]) }, inOut],
      [0.3, { root: [0, 0, 0, 0, 0.02, -0.03], body: [-0.08, 0, 0.05], ...wings([0, 0.2, 0.7], [0, 0.25, 0.6]) }, inOut],
      [0.42, {}, inOut],
    ], { head: 0.02, antennae: 0.035, abdomen: 0.03, hindL: 0.02, hindR: 0.02 });
  }, (t) => {
    // The scales stand on end: it puffs and settles.
    const k = span(t, 0, 0.04, outQuad) * (1 - span(t, 0.08, 0.38, inOut));
    return [1 + 0.1 * k, 1 + 0.08 * k, 1 + 0.08 * k];
  });

/** Dazed: it reels round in a low, lopsided spiral as round a lamp, the wings out of step, antennae drooping. */
const mothStun = (): Clip<B> =>
  clip<B>('stun', 1.6, true, (b, t) => {
    const P = phase(t, 1.6);
    // The right side beats four times, the left three: they never fall in step.
    wingR(b, beatAt(phase(t, 0.4), 0.7 + 0.2 * wave(P, 0.1)));
    wingL(b, beatAt(phase(t + 0.13, 1.6 / 3), 0.5 + 0.2 * wave(P, 0.6)));
    b.root.pos(0.1 * wave(P, 0.25), -0.2 + 0.05 * wave(P, 0, 2), 0.1 * cwave(P, 0.25)).rot(0, 0.5 * wave(P, 0.05), 0);
    b.body.rot(0.1 + 0.12 * wave(P, 0.4, 2), 0, 0.4 * wave(P) + 0.1 * wave(P, 0.2, 3));
    b.head.rot(0.3 + 0.15 * wave(P, 0.15, 2), 0.3 * cwave(P, 0.15), 0.3 * wave(P, 0.15));
    b.antennae.rot(0.5 + 0.12 * wave(P, 0.3, 2), 0, 0.1 * wave(P, 0.2));
    b.abdomen.rot(0.3 + 0.1 * wave(P, 0.5, 2), 0.15 * wave(P, 0.3), 0);
    b.legs.rot(-0.2 + 0.2 * wave(P, 0.35, 2));
    b.dust.rot(0, 0.6 * wave(P), 0).scale(0.75 + 0.08 * wave(P, 0.2, 2));
  });

/** Shot down: it convulses, the wings clap shut over its back, it spirals down like a leaf and lies sprawled. */
const mothDie = (): Clip<B> =>
  clip<B>('die', 1.45, false, (b, t) => {
    const heap: P = {
      root: [0, 2.6, 0, 0.06, GROUND, 0.1], body: [0.1, 0, 0.18], head: [0.35, 0.3, 0.1], antennae: [0.7, 0, 0.1], abdomen: [0.12, 0.2, 0], legs: [-1.3, 0, 0, 0, 0, 0, 0.6], dust: [0, 1.5, 0, 0, 0.04, 0, 0.3],
      foreR: [0.05, 0.2, -0.12], hindR: [0, 0.1, -0.1], foreL: [0.05, 0.35, 0.02], hindL: [0.0, 0.3, 0.12],
    };
    const shut: P = { ...wings([0, 0.35, 1.45], [0, 0.42, 1.4]) };
    timeline(b, t, [
      [0, {}],
      [0.09, { root: [0, 0, 0, 0, 0.08, -0.05], body: [-0.65, 0, 0.15], head: [-0.4, 0, 0], antennae: [-0.5, 0, 0], abdomen: [-0.4, 0, 0], legs: [-0.9, 0, 0], dust: [0, 0, 0, 0, 0, 0, 1.6], ...wings([-0.2, 0.25, 1.3], [-0.1, 0.3, 1.2]) }, outExpo],
      [0.32, { root: [0, 0.9, 0, 0.08, -0.12, 0.04], body: [0.25, 0, 0.45], head: [0.3, 0.2, 0], antennae: [0.4, 0, 0], abdomen: [0.4, 0, 0], legs: [-0.5, 0, 0], dust: [0, 0.6, 0, 0, 0.1, 0, 1.2], ...shut }, inOut],
      // Falling: shut wings like a leaf, turning, the body rolling.
      [0.62, { root: [0, 1.8, 0, -0.06, -0.48, 0.08], body: [0.4, 0, -0.35], head: [0.3, 0, 0], antennae: [0.5, 0, 0], abdomen: [0.3, 0, 0], legs: [-0.6, 0, 0], dust: [0, 1.0, 0, 0, 0.2, 0, 0.8], foreR: [0, 0.3, 1.25], hindR: [0, 0.4, 1.2], foreL: [0, -0.4, -0.95], hindL: [0, -0.45, -0.9] }, inQuad],
      [0.9, { ...heap, root: [0, 2.5, 0, 0.06, GROUND, 0.1], body: [0.15, 0, 0.3], foreR: [0, 0.25, 0.5], hindR: [0, 0.3, 0.45], foreL: [0, -0.3, -0.35], hindL: [0, -0.35, -0.3] }, inQuad],
      [1.02, { ...heap, root: [0, 2.55, 0, 0.06, GROUND + 0.04, 0.1], foreR: [0.05, 0.2, 0.05], hindR: [0, 0.1, 0.02] }, outQuad],
      [1.15, heap, inQuad],
      [1.45, heap, outCubic],
    ], { head: 0.04, antennae: 0.05, abdomen: 0.03, hindL: 0.03, hindR: 0.03, foreR: 0.02, dust: 0.06 });
  }, (t) => {
    // The impact flattens it for a beat.
    const k = Math.max(0, settle(t - 0.9, 3, 9)) * span(t, 0.89, 0.91);
    return [1 + 0.08 * k, 1 - 0.12 * k, 1 + 0.06 * k];
  });

/** Polvo de sueño: one slow, huge wingbeat that sheds a cloud of glowing sleep-dust (the `dust` bone swells and sinks). */
const mothDust = (): Clip<B> =>
  clip<B>('dust', 1.05, false, (b, t) => {
    const high: P = {
      root: [0, 0, 0, 0, 0.06, -0.02], body: [-0.42, 0, 0], head: [-0.15, 0, 0], antennae: [-0.35, 0, 0], abdomen: [0.45, 0, 0], legs: [-0.6, 0, 0], dust: [0, 0.2, 0, 0, 0.02, 0, 0.9],
      ...wings([-0.2, 0.3, 1.32], [-0.12, 0.38, 1.25]),
    };
    timeline(b, t, [
      [0, {}],
      [0.36, high, inOut],
      [0.6, { root: [0, 0, 0, 0, 0.16, 0.04], body: [0.05, 0, 0], head: [0.1, 0, 0], antennae: [0.2, 0, 0], abdomen: [0.1, 0, 0], legs: [-0.2, 0, 0], dust: [0, 0.9, 0, 0, -0.12, 0, 2.5], ...wings([0.32, -0.25, -0.95], [0.25, -0.15, -0.88]) }, outCubic],
      [0.8, { root: [0, 0, 0, 0, 0.1, 0.02], body: [-0.12, 0, 0], abdomen: [0.2, 0, 0], dust: [0, 1.3, 0, 0, -0.2, 0, 2.7], ...wings([-0.05, 0.15, 0.6], [0, 0.2, 0.5]) }, inOut],
      [1.05, {}, inOut],
    ], { head: 0.02, antennae: 0.04, abdomen: 0.04, hindL: 0.03, hindR: 0.03, dust: 0.05 });
  }, (t) => {
    // It gathers itself for the stroke and stretches out through it.
    const k = envelope(t, 0.3, 0.45, 0.6, 0.85, inOut);
    return [1 + 0.03 * k, 1 - 0.02 * k, 1 + 0.03 * k];
  }, 'Polvo de sueño');

/** Its clips (see models/moth.ts for the bones). */
export const mothClips = (): Clip[] => [mothIdle(), mothWalk(), mothAttack(), mothHit(), mothStun(), mothDie(), mothDust()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { moth: mothClips };
